(() => {
  const initOluReviews = (root, reduceMotion) => {
    const carousel = root.querySelector('[data-olu-reviews]');
    if (!carousel || carousel.dataset.oluReviewsReady === 'true') return;
    carousel.dataset.oluReviewsReady = 'true';

    const stage = carousel.querySelector('.olu-about__review-stage');
    const track = carousel.querySelector('.olu-about__review-track');
    const slides = Array.from(carousel.querySelectorAll('[data-review-slide]'));
    const previous = carousel.querySelector('.olu-about__review-arrow--prev');
    const next = carousel.querySelector('.olu-about__review-arrow--next');
    const count = carousel.querySelector('.olu-about__review-count b');
    const desktop = window.matchMedia('(min-width: 990px)');
    let activeIndex = 0;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let isDragging = false;
    let wheelLock = false;

    const positionFor = (index) => {
      const total = slides.length;
      const distance = (index - activeIndex + total) % total;
      if (distance === 0) return 'active';
      if (distance === 1) return 'next';
      if (distance === total - 1) return 'prev';
      if (distance === 2) return 'far-right';
      return 'far-left';
    };

    const sync = (shouldScroll = false) => {
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.dataset.reviewPosition = positionFor(index);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.tabIndex = isActive ? 0 : -1;
      });

      if (count) count.textContent = String(activeIndex + 1).padStart(2, '0');

      if (!desktop.matches && shouldScroll) {
        slides[activeIndex].scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    };

    const select = (index, shouldScroll = false) => {
      activeIndex = (index + slides.length) % slides.length;
      root.style.setProperty('--review-shift-x', '0px');
      root.style.setProperty('--review-shift-y', '0px');
      root.style.setProperty('--review-tilt', '0deg');
      sync(shouldScroll);
    };

    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (index !== activeIndex) select(index, !desktop.matches);
      });

      slide.addEventListener('focus', () => {
        if (index !== activeIndex) select(index, !desktop.matches);
      });
    });

    previous?.addEventListener('click', () => select(activeIndex - 1, !desktop.matches));
    next?.addEventListener('click', () => select(activeIndex + 1, !desktop.matches));

    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        select(activeIndex - 1, !desktop.matches);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        select(activeIndex + 1, !desktop.matches);
      }
    });

    stage?.addEventListener('wheel', (event) => {
      if (!desktop.matches || Math.abs(event.deltaY) < 10 || wheelLock) return;
      event.preventDefault();
      wheelLock = true;
      select(activeIndex + (event.deltaY > 0 ? 1 : -1));
      window.setTimeout(() => { wheelLock = false; }, 620);
    }, { passive: false });

    stage?.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      isDragging = true;
      stage.setPointerCapture?.(event.pointerId);
    });

    stage?.addEventListener('pointerup', (event) => {
      if (!isDragging) return;
      const dx = event.clientX - pointerStartX;
      const dy = event.clientY - pointerStartY;
      isDragging = false;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        select(activeIndex + (dx < 0 ? 1 : -1), !desktop.matches);
      }
    });

    stage?.addEventListener('pointercancel', () => { isDragging = false; });

    if (!reduceMotion && window.matchMedia('(pointer: fine)').matches) {
      stage?.addEventListener('pointermove', (event) => {
        if (!desktop.matches) return;
        const active = slides[activeIndex];
        const rect = active.getBoundingClientRect();
        const x = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1));
        const y = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1));
        root.style.setProperty('--review-shift-x', `${x * 6}px`);
        root.style.setProperty('--review-shift-y', `${y * 4}px`);
        root.style.setProperty('--review-tilt', `${x * 1.1}deg`);
      });

      stage?.addEventListener('pointerleave', () => {
        root.style.setProperty('--review-shift-x', '0px');
        root.style.setProperty('--review-shift-y', '0px');
        root.style.setProperty('--review-tilt', '0deg');
      });
    }

    let mobileScrollFrame = 0;
    track?.addEventListener('scroll', () => {
      if (desktop.matches || mobileScrollFrame) return;
      mobileScrollFrame = requestAnimationFrame(() => {
        const trackCenter = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        slides.forEach((slide, index) => {
          const cardCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(cardCenter - trackCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = index;
          }
        });
        if (closest !== activeIndex) select(closest, false);
        mobileScrollFrame = 0;
      });
    }, { passive: true });

    desktop.addEventListener?.('change', () => sync(false));
    sync(false);
  };

  const initOluAbout = (root) => {
    if (!root || root.dataset.oluReady === 'true') return;
    root.dataset.oluReady = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealItems = root.querySelectorAll('[data-olu-reveal]');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      const observer = new IntersectionObserver(
        (entries, observerInstance) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observerInstance.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -32px 0px' }
      );
      revealItems.forEach((item) => observer.observe(item));
    }

    const hero = root.querySelector('.olu-about__hero');
    const finePointer = window.matchMedia('(pointer: fine)').matches;

    if (hero && finePointer && !reduceMotion) {
      hero.addEventListener('pointermove', (event) => {
        const bounds = hero.getBoundingClientRect();
        root.style.setProperty('--olu-px', ((event.clientX - bounds.left) / bounds.width - 0.5).toFixed(3));
        root.style.setProperty('--olu-py', ((event.clientY - bounds.top) / bounds.height - 0.5).toFixed(3));
      });
      hero.addEventListener('pointerleave', () => {
        root.style.setProperty('--olu-px', '0');
        root.style.setProperty('--olu-py', '0');
      });
    }

    let frame = 0;
    const updateProgress = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const sectionTop = root.getBoundingClientRect().top + window.scrollY;
        const scrollableDistance = root.offsetHeight - window.innerHeight;
        const progress = scrollableDistance > 0 ? ((window.scrollY - sectionTop) / scrollableDistance) * 100 : 0;
        root.style.setProperty('--olu-scroll', `${Math.max(0, Math.min(100, progress))}%`);
        frame = 0;
      });
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    initOluReviews(root, reduceMotion);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-olu-about]').forEach(initOluAbout);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
